import { useState, useCallback, useMemo, useEffect } from "react";
import { useRouter } from "next/router";
import { Row, Col, Spin, message } from "antd";
import { PaymentElement } from "@stripe/react-stripe-js";
import {
  RightOutlined,
  Loading3QuartersOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import { PaymentStepAssets } from "@dreambigger/shared/src/types";
import {
  Button,
  Form,
  Checkbox,
} from "@dreambigger/design-system/src/components";
import {
  StepContentSection,
  StripePaymentForm,
  StepFooterText,
} from "../components";
import { StepProps } from "../pages/flows/[flowId]";
import { useApplication } from "../api";
import styles from "../steps/steps.module.scss";
import { useAuthToken } from "../passwordless-auth";
import { default as StripeApi } from "@dreambigger/shared/src/api/stripe";
import { api } from "@dreambigger/shared/src/api/acquire";
import Handlebars from "handlebars";

export const confirmationKey = "paymentConfirmation";
const spinnerIcon = <Loading3QuartersOutlined spin />;

// Payment statuses that indicate forward progress (payment has been submitted)
export const FORWARD_PROGRESS_STATUSES = [
  "succeeded",
  "requires_capture",
  "processing",
  "requires_action",
] as const;
export type ForwardProgressStatus = typeof FORWARD_PROGRESS_STATUSES[number];

// Type guard to check if a status is a forward progress status
const isForwardProgressStatus = (
  status: string | undefined
): status is ForwardProgressStatus => {
  return (
    status !== undefined &&
    FORWARD_PROGRESS_STATUSES.includes(status as ForwardProgressStatus)
  );
};

export const paymentIntentSecretKey = "paymentIntentSecret";
export const paymentIntentIdKey = "paymentIntentId";
export const paymentIntentAmountKey = "paymentIntentAmount";
export const paymentIntentOptInKey = "paymentIntentOptIn";
export const paymentIntentRecipientKey = "paymentIntentRecipient";
export const paymentIntentIntervalKey = "paymentIntentInterval";
export const paymentIntentIntervalCountKey = "paymentIntentIntervalCount";
export const paymentIntentStatusKey = "paymentIntentStatus";

const stripeApi = new StripeApi(api);

// Mapping for recurring frequency presets to interval counts by unit type
const FREQUENCY_MAPPINGS: Record<string, Record<string, number>> = {
  month: {
    monthly: 1,
    bimonthly: 2,
    quarterly: 3,
    semiannually: 6,
    "semi-annually": 6,
    annual: 12,
    annually: 12,
    yearly: 12,
  },
  // TODO: Add mappings for other interval units when needed
  week: {},
  day: {},
  year: {},
};

// Helper function to convert frequency strings to interval counts
const getIntervalCount = (
  value: string | number | undefined,
  intervalUnit?: "day" | "week" | "month" | "year" | string
): number | undefined => {
  if (value === undefined || value === null) return undefined;

  // If it's already a number, return it
  if (typeof value === "number") return value;

  // Convert to string and lowercase for lookup
  const stringValue = String(value).toLowerCase();

  // Get the appropriate frequency map based on the interval unit
  // Only apply mappings if we have a valid unit
  const frequencyMap = intervalUnit
    ? FREQUENCY_MAPPINGS[intervalUnit] || {}
    : {};

  // Check if it's a preset value for this unit
  if (frequencyMap[stringValue]) {
    return frequencyMap[stringValue];
  }

  // Otherwise try to parse as number (for legacy or custom numeric values)
  const parsed = Number(stringValue);
  return isNaN(parsed) ? undefined : parsed;
};

export default function Payment({ flow, step, brand, progress }: StepProps) {
  const [disabled, setDisabled] = useState(true);
  const [elementComplete, setElementComplete] = useState(false);
  const [elementLoading, setElementLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [currentClientSecret, setCurrentClientSecret] =
    useState<string | undefined>();
  const [creatingPaymentIntent, setCreatingPaymentIntent] = useState(false);
  const router = useRouter();
  const { slug, type } = step;
  const tokenHelper = useAuthToken(flow.financialInstitution.id);
  const applicationHelper = useApplication(flow.financialInstitution.id);
  const application = useMemo(
    () => applicationHelper.find(flow.id, "draft"),
    [applicationHelper, flow]
  );

  const { flowId } = router.query;
  const assets: PaymentStepAssets = step.assets;
  const {
    paymentAmountSlug,
    paymentAmountFieldKey,
    subscriptionOptInSlug,
    subscriptionOptInFieldKey,
    confirmationText,
    stepFooterText,
    autoConfirmation,
    submitButtonText,
    stripeAccountId,
    nextSlug,
    stripePaymentIntentConfigurationId,
    stripeSubscriptionConfigurationId,
    stripeSetupIntentConfigurationId,
    subscriptionTitleConfiguration,
    subscriptionDescriptionConfiguration,
    recipientSlug,
    recipientFieldKey,
    recurringIntervalSlug,
    recurringIntervalFieldKey,
    recurringIntervalCountSlug,
    recurringIntervalCountFieldKey,
    customRecurringIntervalCountSlug,
    customRecurringIntervalCountFieldKey,
    defaultRecurringIntervalUnit,
    stripeProductId,
  } = assets;

  const paymentAmount: number | undefined = useMemo(() => {
    if (!paymentAmountSlug || !paymentAmountFieldKey) {
      return;
    }
    const amount = applicationHelper.findResponseField(
      flow.id,
      "draft",
      paymentAmountSlug,
      paymentAmountFieldKey
    );
    if (!amount) {
      return;
    }
    return Number(amount);
  }, [applicationHelper, flow.id, paymentAmountFieldKey, paymentAmountSlug]);

  const savedAmount: number | undefined = useMemo(() => {
    const amount = applicationHelper.findResponseField(
      flow.id,
      "draft",
      slug,
      paymentIntentAmountKey
    );
    if (!amount) {
      return;
    }
    return Number(amount);
  }, [applicationHelper, flow.id, slug]);

  // Extract recipient ID from previous step
  // Used for directing subscriptions to specific recipients in multi-recipient flows
  // Multiple slugs are supported because the same recipient can be captured by
  // different steps depending on how the applicant entered the flow.
  const recipientId: string | undefined = useMemo(() => {
    if (!recipientSlug || !recipientFieldKey) {
      return;
    }
    const candidateSlugs = Array.isArray(recipientSlug)
      ? recipientSlug
      : [recipientSlug];

    for (const candidateSlug of candidateSlugs) {
      const recipient = applicationHelper.findResponseField(
        flow.id,
        "draft",
        candidateSlug,
        recipientFieldKey
      );

      if (recipient) {
        return String(recipient);
      }
    }

    return undefined;
  }, [applicationHelper, flow.id, recipientFieldKey, recipientSlug]);

  // TODO (FUTURE ENHANCEMENT): Handle dynamic interval selection where users can choose the interval type (day/week/month/year)
  // Extract interval type (day/week/month/year) from previous step
  // Used when users can dynamically select the interval type
  // Will be overridden by defaultRecurringIntervalUnit if that's configured
  // Currently we support defaultRecurringIntervalUnit for fixed intervals and this field for dynamic selection
  // In the future, we may need to handle more complex scenarios with both interval type and count from form fields
  const recurringInterval: string | undefined = useMemo(() => {
    if (!recurringIntervalSlug || !recurringIntervalFieldKey) {
      return;
    }
    const interval = applicationHelper.findResponseField(
      flow.id,
      "draft",
      recurringIntervalSlug,
      recurringIntervalFieldKey
    );
    return interval ? String(interval) : undefined;
  }, [
    applicationHelper,
    flow.id,
    recurringIntervalFieldKey,
    recurringIntervalSlug,
  ]);

  // Extract and convert interval count from previous step
  // Handles both string values (e.g., "quarterly") and numeric values (e.g., 3)
  // Uses FREQUENCY_MAPPINGS to convert preset strings to numbers based on interval unit
  // If customRecurringIntervalCountFieldKey is configured, checks that field when main field returns undefined
  const recurringIntervalCount: number | undefined = useMemo(() => {
    if (!recurringIntervalCountSlug || !recurringIntervalCountFieldKey) {
      return;
    }
    const count = applicationHelper.findResponseField(
      flow.id,
      "draft",
      recurringIntervalCountSlug,
      recurringIntervalCountFieldKey
    );
    // Pass the interval unit so we know which mappings to apply
    // E.g., "quarterly" maps to 3 for months, but wouldn't map for weeks
    const intervalUnit = defaultRecurringIntervalUnit || recurringInterval;
    const intervalCount = getIntervalCount(
      count as string | number | undefined,
      intervalUnit
    );

    // If main field didn't provide a valid count and custom field is configured, try custom field
    if (
      intervalCount === undefined &&
      customRecurringIntervalCountSlug &&
      customRecurringIntervalCountFieldKey
    ) {
      const customCount = applicationHelper.findResponseField(
        flow.id,
        "draft",
        customRecurringIntervalCountSlug,
        customRecurringIntervalCountFieldKey
      );
      return getIntervalCount(
        customCount as string | number | undefined,
        intervalUnit
      );
    }

    return intervalCount;
  }, [
    applicationHelper,
    flow.id,
    recurringIntervalCountFieldKey,
    recurringIntervalCountSlug,
    customRecurringIntervalCountSlug,
    customRecurringIntervalCountFieldKey,
    defaultRecurringIntervalUnit,
    recurringInterval,
  ]);

  const subscriptionOptIn: boolean | undefined = useMemo(() => {
    if (!subscriptionOptInSlug || !subscriptionOptInFieldKey) {
      return;
    }
    const optIn = applicationHelper.findResponseField(
      flow.id,
      "draft",
      subscriptionOptInSlug,
      subscriptionOptInFieldKey
    );
    if (!optIn) {
      return;
    }
    return Boolean(optIn);
  }, [
    applicationHelper,
    flow.id,
    subscriptionOptInFieldKey,
    subscriptionOptInSlug,
  ]);

  const savedOptIn: boolean | undefined = useMemo(() => {
    const optIn = applicationHelper.findResponseField(
      flow.id,
      "draft",
      slug,
      paymentIntentOptInKey
    );
    if (!optIn) {
      return;
    }
    return Boolean(optIn);
  }, [applicationHelper, flow.id, slug]);

  const savedRecipient: string | undefined = useMemo(() => {
    const recipient = applicationHelper.findResponseField(
      flow.id,
      "draft",
      slug,
      paymentIntentRecipientKey
    );
    return recipient ? String(recipient) : undefined;
  }, [applicationHelper, flow.id, slug]);

  const savedInterval: string | undefined = useMemo(() => {
    const interval = applicationHelper.findResponseField(
      flow.id,
      "draft",
      slug,
      paymentIntentIntervalKey
    );
    return interval ? String(interval) : undefined;
  }, [applicationHelper, flow.id, slug]);

  const savedIntervalCount: number | undefined = useMemo(() => {
    const count = applicationHelper.findResponseField(
      flow.id,
      "draft",
      slug,
      paymentIntentIntervalCountKey
    );
    return count ? Number(count) : undefined;
  }, [applicationHelper, flow.id, slug]);

  const billingDetails = useMemo(() => {
    if (!assets.billingDetails) {
      return;
    }
    const { name, email, phone, address } = assets.billingDetails;
    const firstName = applicationHelper.findResponseField(
      flow.id,
      "draft",
      name?.slug,
      name?.firstNameFieldKey
    );
    const lastName = applicationHelper.findResponseField(
      flow.id,
      "draft",
      name?.slug,
      name?.lastNameFieldKey
    );
    return {
      defaultValues: {
        billingDetails: {
          name: firstName && lastName ? `${firstName} ${lastName}` : "",
          email: String(
            applicationHelper.findResponseField(
              flow.id,
              "draft",
              email?.slug,
              email?.fieldKey
            ) || ""
          ),
          phone: String(
            applicationHelper.findResponseField(
              flow.id,
              "draft",
              phone?.slug,
              phone?.fieldKey
            ) || ""
          ),
          address: {
            line1: String(
              applicationHelper.findResponseField(
                flow.id,
                "draft",
                address?.slug,
                address?.line1FieldKey
              ) || ""
            ),
            line2: String(
              applicationHelper.findResponseField(
                flow.id,
                "draft",
                address?.slug,
                address?.line2FieldKey
              ) || ""
            ),
            city: String(
              applicationHelper.findResponseField(
                flow.id,
                "draft",
                address?.slug,
                address?.cityFieldKey
              ) || ""
            ),
            state: String(
              applicationHelper.findResponseField(
                flow.id,
                "draft",
                address?.slug,
                address?.stateFieldKey
              ) || ""
            ),
            country: "US",
            postal_code: String(
              applicationHelper.findResponseField(
                flow.id,
                "draft",
                address?.slug,
                address?.postalCodeFieldKey
              ) || ""
            ),
          },
        },
      },
    };
  }, [applicationHelper, flow, assets]);

  // Compile dynamic subscription title using Handlebars
  const subscriptionTitle = useMemo(() => {
    if (!stripeSubscriptionConfigurationId) {
      return;
    }
    if (!subscriptionTitleConfiguration) return flow.skins?.[0]?.title;
    if (subscriptionTitleConfiguration.type === "static") {
      return subscriptionTitleConfiguration.template;
    }
    if (
      subscriptionTitleConfiguration.type === "dynamic" &&
      subscriptionTitleConfiguration.template
    ) {
      try {
        // Build data object from values configuration
        const templateData: Record<string, any> = {};
        subscriptionTitleConfiguration.values?.forEach((valueConfig) => {
          const value = applicationHelper.findResponseField(
            flow.id,
            "draft",
            valueConfig.slug,
            valueConfig.fieldKey
          );
          if (value !== undefined) {
            // Use stepSlug__fieldKey pattern
            const templateKey = `${valueConfig.slug}__${valueConfig.fieldKey}`;
            templateData[templateKey] = value;
          }
        });

        // If no values were found, use defaultValue
        if (Object.keys(templateData).length === 0) {
          return (
            subscriptionTitleConfiguration.defaultValue ||
            flow.skins?.[0]?.title
          );
        }

        // Compile and execute the template
        const compiledTemplate = Handlebars.compile(
          subscriptionTitleConfiguration.template
        );
        return compiledTemplate(templateData);
      } catch (error) {
        console.error("Error compiling subscription title template:", error);
        return (
          subscriptionTitleConfiguration.defaultValue || flow.skins?.[0]?.title
        );
      }
    }
    return "";
  }, [
    subscriptionTitleConfiguration,
    stripeSubscriptionConfigurationId,
    flow.skins,
    applicationHelper,
    flow.id,
  ]);

  // Compile dynamic subscription description using Handlebars
  const subscriptionDescription = useMemo(() => {
    if (!stripeSubscriptionConfigurationId) {
      return;
    }
    if (!subscriptionDescriptionConfiguration) return flow.skins?.[0]?.title;
    if (subscriptionDescriptionConfiguration.type === "static") {
      return subscriptionDescriptionConfiguration.template;
    }
    if (
      subscriptionDescriptionConfiguration.type === "dynamic" &&
      subscriptionDescriptionConfiguration.template
    ) {
      try {
        // Build data object from values configuration
        const templateData: Record<string, any> = {};
        subscriptionDescriptionConfiguration.values?.forEach((valueConfig) => {
          const value = applicationHelper.findResponseField(
            flow.id,
            "draft",
            valueConfig.slug,
            valueConfig.fieldKey
          );
          if (value !== undefined) {
            // Use stepSlug__fieldKey pattern to avoid collisions across steps
            const templateKey = `${valueConfig.slug}__${valueConfig.fieldKey}`;
            templateData[templateKey] = value;
          }
        });

        // If no values were found, use defaultValue
        if (Object.keys(templateData).length === 0) {
          return (
            subscriptionDescriptionConfiguration.defaultValue ||
            flow.skins?.[0]?.title
          );
        }

        // Compile and execute the template
        const compiledTemplate = Handlebars.compile(
          subscriptionDescriptionConfiguration.template
        );
        return compiledTemplate(templateData);
      } catch (error) {
        console.error(
          "Error compiling subscription description template:",
          error
        );
        return (
          subscriptionDescriptionConfiguration.defaultValue ||
          flow.skins?.[0]?.title
        );
      }
    }
    return "";
  }, [
    stripeSubscriptionConfigurationId,
    subscriptionDescriptionConfiguration,
    flow.skins,
    applicationHelper,
    flow.id,
  ]);

  const savedClientSecret: string | undefined = useMemo(() => {
    const paymentIntentSecret = applicationHelper.findResponseField(
      flow.id,
      "draft",
      slug,
      paymentIntentSecretKey
    );
    return paymentIntentSecret ? String(paymentIntentSecret) : undefined;
  }, [applicationHelper, flow.id, slug]);

  const savedStatus: string | undefined = useMemo(() => {
    const status = applicationHelper.findResponseField(
      flow.id,
      "draft",
      slug,
      paymentIntentStatusKey
    );
    return status ? String(status) : undefined;
  }, [applicationHelper, flow.id, slug]);

  const handlePaymentInput = useCallback(
    (changedValues: any, values: any) => {
      if (autoConfirmation) {
        setConfirmed(true);
        setDisabled(!elementComplete);
        return;
      }
      setConfirmed(values[confirmationKey]);
      setDisabled(!(values[confirmationKey] && elementComplete));
    },
    [elementComplete, autoConfirmation]
  );

  //generate paymeooiint intent secret and save to application
  useEffect(() => {
    if (!application) {
      return;
    }

    // If payment has already been submitted (has a status), don't recreate payment intent
    if (savedStatus) {
      return;
    }

    //skip payment intent creation if payment intent has already been created
    //and payment amount, subscription opt in, recipient, or recurring settings have not changed
    const intervalUnit = defaultRecurringIntervalUnit || recurringInterval;

    if (
      savedClientSecret &&
      (!paymentAmountFieldKey ||
        !paymentAmountSlug ||
        paymentAmount === savedAmount) &&
      (!subscriptionOptInFieldKey ||
        !subscriptionOptInSlug ||
        subscriptionOptIn === savedOptIn) &&
      (!recipientFieldKey ||
        !recipientSlug ||
        recipientId === savedRecipient) &&
      (!recurringIntervalFieldKey ||
        !recurringIntervalSlug ||
        intervalUnit === savedInterval) &&
      (!recurringIntervalCountFieldKey ||
        !recurringIntervalCountSlug ||
        recurringIntervalCount === savedIntervalCount)
    ) {
      setCurrentClientSecret(savedClientSecret);
      setCreatingPaymentIntent(false);
      return;
    }

    //FUTURE: clean up association with old payment intent, subscription and application
    //if (savedClientSecret) {
    //cancel payment intent, subscription (if any) and clear assocciation with application here
    //}

    //wait if payment intent is being created
    if (creatingPaymentIntent) {
      return;
    }

    //create new payment intent
    setCreatingPaymentIntent(true);

    // Validate that interval unit and count are provided together (only for subscriptions)
    if (stripeSubscriptionConfigurationId && subscriptionOptIn) {
      if (
        (intervalUnit && !recurringIntervalCount) ||
        (!intervalUnit && recurringIntervalCount)
      ) {
        message.error(
          "Payment configuration error: Both recurring interval and interval count must be provided together"
        );
        setCreatingPaymentIntent(false);
        return;
      }
    }

    const priceData =
      stripeSubscriptionConfigurationId &&
      subscriptionOptIn &&
      intervalUnit &&
      recurringIntervalCount &&
      stripeProductId
        ? [
            {
              currency: "usd",
              product: stripeProductId,
              recurring: {
                interval: intervalUnit as "day" | "week" | "month" | "year",
                interval_count: recurringIntervalCount,
              },
              unitAmount: paymentAmount,
            },
          ]
        : undefined;

    stripeApi
      .createPaymentIntent(
        !stripeSubscriptionConfigurationId || !subscriptionOptIn
          ? stripePaymentIntentConfigurationId
          : undefined,
        stripeSubscriptionConfigurationId && subscriptionOptIn
          ? stripeSubscriptionConfigurationId
          : undefined,
        stripeSubscriptionConfigurationId &&
          subscriptionOptIn &&
          stripeSetupIntentConfigurationId
          ? stripeSetupIntentConfigurationId
          : undefined,
        flow.id,
        "Flow",
        tokenHelper,
        paymentAmount,
        application.id,
        "OnboardingApplication",
        subscriptionTitle,
        subscriptionDescription,
        recipientId,
        priceData
      )
      .then((res) => res.data)
      .then(async ({ id, clientSecret }) => {
        const saved = await applicationHelper.update(application.id, {
          slug,
          type,
          fields: {
            [paymentIntentSecretKey]: clientSecret,
            [paymentIntentIdKey]: id,
            [paymentIntentAmountKey]: paymentAmount || "",
            [paymentIntentOptInKey]: subscriptionOptIn || "",
            [paymentIntentRecipientKey]: recipientId || "",
            [paymentIntentIntervalKey]: intervalUnit || "",
            [paymentIntentIntervalCountKey]: recurringIntervalCount || "",
          },
        });
        if (!saved) {
          //TODO: handle scenario if the application is not saved
          return;
        }
      })
      .catch(() => message.error("Error initializing payment"));
  }, [
    application,
    creatingPaymentIntent,
    savedClientSecret,
    stripePaymentIntentConfigurationId,
    stripeSubscriptionConfigurationId,
    stripeSetupIntentConfigurationId,
    subscriptionOptIn,
    subscriptionOptInFieldKey,
    subscriptionOptInSlug,
    savedOptIn,
    flow.id,
    tokenHelper,
    paymentAmount,
    paymentAmountFieldKey,
    paymentAmountSlug,
    savedAmount,
    applicationHelper,
    slug,
    type,
    subscriptionTitle,
    subscriptionDescription,
    recipientId,
    recipientFieldKey,
    recipientSlug,
    savedRecipient,
    recurringInterval,
    recurringIntervalFieldKey,
    recurringIntervalSlug,
    savedInterval,
    recurringIntervalCount,
    recurringIntervalCountFieldKey,
    recurringIntervalCountSlug,
    savedIntervalCount,
    defaultRecurringIntervalUnit,
    stripeProductId,
    savedStatus,
  ]);

  if (!applicationHelper || !application) {
    return <Spin indicator={spinnerIcon} />;
  }

  // Check if payment has been submitted (any forward-progress status)
  const isPaymentSubmitted = isForwardProgressStatus(savedStatus);

  // Determine if we should show the payment form or submitted state
  const showPaymentForm =
    !isPaymentSubmitted &&
    savedClientSecret &&
    currentClientSecret &&
    savedClientSecret === currentClientSecret;
  const showLoadingSpinner = !isPaymentSubmitted && !showPaymentForm;

  return (
    <StepContentSection
      brand={brand}
      stepProgress={progress}
      title={assets.title ?? ""}
      description={assets.description ?? ""}
      previousSlug={assets.previousSlug}
      hideProgressBar={assets.hideProgressBar}
    >
      {isPaymentSubmitted && (
        <div
          className="flex items-center justify-center f-6 md_f-7 mb-7"
          style={{ color: "green", minHeight: "30vh" }}
        >
          <CheckCircleFilled />
          <p className="ml-2 mb-0">Payment Submitted</p>
        </div>
      )}

      {showPaymentForm && (
        <StripePaymentForm
          onValuesChange={handlePaymentInput}
          clientSecret={savedClientSecret}
          intentType="payment"
          returnUrl={`${window.location.origin}/flows/${flowId}?stepSlug=${nextSlug}`}
          appearance={{
            theme: "stripe",
            variables: {
              colorPrimary: brand.primaryColor,
              borderRadius: "5px",
            },
          }}
          stripeAccount={stripeAccountId}
        >
          <Row>
            <Col xs={24} sm={24} md={16} lg={16} xl={16} xxl={16}>
              {elementLoading && (
                <div>
                  <Spin indicator={spinnerIcon} />
                </div>
              )}
              <PaymentElement
                onChange={(e) => {
                  setElementComplete(e.complete);
                  setDisabled(!((autoConfirmation || confirmed) && e.complete));
                }}
                onReady={() => setElementLoading(false)}
                options={billingDetails}
              />
            </Col>
          </Row>
          <div className={`mt-6 flex`}>
            {!autoConfirmation && (
              <div className="pr-3">
                <Form.Item name={confirmationKey} valuePropName="checked">
                  <Checkbox defaultChecked={false} className="ao-lg-checkbox" />
                </Form.Item>
              </div>
            )}
            <div>
              <p
                className={`${styles.textLinks} f-4 gray-8 lh-4 mb-6`}
                dangerouslySetInnerHTML={{
                  __html: confirmationText ?? "",
                }}
              />
            </div>
          </div>
          <div className="mt-6 pt-4 bt b-lightPrimary">
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                disabled={disabled}
                className="s-2 lift h-7 ph-5"
              >
                {submitButtonText} <RightOutlined />
              </Button>
            </Form.Item>
            <StepFooterText text={stepFooterText} />
          </div>
        </StripePaymentForm>
      )}

      {showLoadingSpinner && (
        <div>
          <Spin indicator={spinnerIcon} />
        </div>
      )}

      {isPaymentSubmitted && (
        <div className="mt-6 pt-4 bt b-lightPrimary">
          <Button
            type="primary"
            onClick={() => router.push(`/flows/${flowId}?stepSlug=${nextSlug}`)}
            className="s-2 lift h-7 ph-5"
          >
            Continue <RightOutlined />
          </Button>
        </div>
      )}
    </StepContentSection>
  );
}
